import { useMemo } from 'react';
import type { Aop, PerformanceStatus, RangeMetrics, Territory } from '../types/dashboard';
import { useFilters } from '../contexts/FilterContext';
import { makeCohortScope, makeScope } from '../utils/filters';
import { resolveRange, shiftRange } from '../utils/calendar';
import { computeGrouped, computeSeries, emptySeriesSet, perUnitRange, rangeMetrics } from '../utils/metrics';
import { SEASON_KPI_BASE, aggregateRetention, retentionSummaries } from '../utils/retention';
import { ALL_TERRITORY_IDS, activeAopCount, activeTerritoryCount } from '../utils/territory';
import { buildBenchmarkItems } from '../utils/benchmarks';
import { buildMetricKpis } from '../utils/kpis';
import { change, monthValue, rangeValue, type TrendMetric } from '../utils/metricConfig';
import { isBelowStatus, performanceStatus } from '../utils/status';

export interface AopRow {
  aop: Aop;
  metrics: RangeMetrics;
  value: number;
  vsTerritory: number;
  vsNetwork: number;
  mom: number | null;
  yoy: number | null;
  retention: number | null;
  status: PerformanceStatus;
}

export function useEntityData(territory: Territory, aop: Aop | undefined, metric: TrendMetric) {
  const { filters } = useFilters();
  return useMemo(() => {
    const range = resolveRange(filters);
    const lyRange = shiftRange(range, -12);
    const { start, end } = range;
    const scope = makeScope(filters, [territory.id], aop ? [aop.id] : null);
    const territoryScope = makeScope(filters, [territory.id], null);
    const series = computeSeries(scope);
    const network = computeSeries(makeScope(filters, ALL_TERRITORY_IDS, null));
    const active = (m: number) => territory.launchMonth <= m;
    const terrUnits = (m: number) => (active(m) ? activeTerritoryCount(ALL_TERRITORY_IDS, m) : 0);
    const aopUnits = (m: number) => (active(m) ? activeAopCount(ALL_TERRITORY_IDS, m) : 0);
    const unitsFn = aop ? aopUnits : terrUnits;

    const metrics = rangeMetrics(series.all, start, end);
    const bench = perUnitRange(network.all, start, end, unitsFn);
    const gap = change(metric, rangeValue(metric, metrics), rangeValue(metric, bench)) ?? 0;
    const status = performanceStatus(metric, gap, end - territory.launchMonth);

    const cohort = makeCohortScope(filters, [territory.id], aop ? [aop.id] : null);
    const territoryCohort = makeCohortScope(filters, [territory.id], null);
    const networkCohort = makeCohortScope(filters, ALL_TERRITORY_IDS, null);
    const retention = retentionSummaries(cohort, networkCohort);

    const benchmarkItems = buildBenchmarkItems({
      entity: metrics,
      bench,
      seasonRet: { entity: retention.season.rate, network: retention.season.network },
      yoyRet: { entity: retention.yoy.rate, network: retention.yoy.network },
    });

    // AOP comparison (always every AOP in the territory)
    const territorySeries = computeSeries(territoryScope);
    const territoryAvgValue = rangeValue(metric, perUnitRange(territorySeries.all, start, end, (m) => (active(m) ? territory.aops.length : 0)));
    const aopBenchValue = rangeValue(metric, perUnitRange(network.all, start, end, aopUnits));
    const grouped = computeGrouped(territoryScope, (r) => r.a);
    const aopRetention = aggregateRetention(territoryCohort, SEASON_KPI_BASE, SEASON_KPI_BASE + 1, false, (r) => r.a);
    const aopRows: AopRow[] = territory.aops.map((a) => {
      const set = grouped.get(a.id) ?? emptySeriesSet();
      const m = rangeMetrics(set.all, start, end);
      const value = rangeValue(metric, m);
      const ly = lyRange && territory.launchMonth <= lyRange.start ? rangeValue(metric, rangeMetrics(set.all, lyRange.start, lyRange.end)) : null;
      const vsNetwork = change(metric, value, aopBenchValue) ?? 0;
      const ret = aopRetention.get(a.id);
      return {
        aop: a,
        metrics: m,
        value,
        vsTerritory: change(metric, value, territoryAvgValue) ?? 0,
        vsNetwork,
        mom: active(end - 1) ? change(metric, monthValue(metric, set.all, end), monthValue(metric, set.all, end - 1)) : null,
        yoy: change(metric, value, ly),
        retention: ret && ret.base ? ret.rate : null,
        status: performanceStatus(metric, vsNetwork, end - territory.launchMonth),
      };
    });

    const kpis = buildMetricKpis({
      metric,
      series,
      network,
      range,
      lyRange,
      scopeUnits: (m) => (active(m) ? 1 : 0),
      networkUnits: unitsFn,
      unitLabel: aop ? 'AOP' : 'territory',
      single: true,
      scopeLabel: aop ? aop.name : territory.name,
      below: { count: aopRows.filter((r) => isBelowStatus(r.status)).length, total: aopRows.length, noun: 'AOPs' },
    });

    return {
      range,
      series,
      network,
      metrics,
      kpis,
      benchmarkItems,
      retention,
      status,
      gap,
      aopRows,
      aopBenchValue,
      territoryAvgValue,
      scopeUnits: (m: number) => (active(m) ? 1 : 0),
      networkUnits: unitsFn,
      cohort,
      networkCohort,
    };
  }, [filters, territory, aop, metric]);
}
